import { useImperativeHandle, forwardRef, useMemo, useState } from 'react';
import { CheckOutlined, StarFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { formatDate } from '../../../utils/dateFormatter';
import './plancard.css';
import {
  type SubscriptionPreview,
} from '@/api/payment';

import { type PaymentPlanV3 } from '@/api/payment';

interface PlanCardProps {
  id: string;
  isSelected: boolean;
  onSelect: () => void;
  colorClass: string;
  isBestValue?: boolean;
  subscriptionPreview: SubscriptionPreview;
  paymentPlan: PaymentPlanV3;
}

export interface PlanCardRef {
  getCalculatedValues: () => {
    tokenAmount: string;
    remainingAmount: string;
    discountAmount: number;
    discountPercent: number;
    totalAmount: number;
    payNowAmount: number;
  };
}

export const PlanCard = forwardRef<PlanCardRef, PlanCardProps>(({
  isSelected,
  onSelect,
  colorClass,
  isBestValue,
  subscriptionPreview,
  paymentPlan: paymentPlanFromProp,
}, ref) => {
  const { token, paymentCalendar, paymentPlan, totalAmount, mrp } = subscriptionPreview;
  const isOneTime = paymentPlan?.planType === 'ONE_TIME';
  const isPartPayment = paymentPlan?.planType === 'PART_PAYMENT';
  const hasToken = token && (token.amount ?? 0) > 0;

  const discountPercent = (paymentPlan?.discount || 0) * 100;

  // Determine payNow amount
  let payNowAmount = 0;
  if (hasToken) {
    payNowAmount = token!.amount ?? 0;
  } else if (isPartPayment && (paymentCalendar?.installments?.length ?? 0) > 0) {
    payNowAmount = paymentCalendar!.installments![0].amount ?? 0;
  } else if (isOneTime) {
    payNowAmount = totalAmount ?? 0;
  }

  // Build payment schedule items
  const scheduleItems = useMemo(() => {
    const items = [];

    // Token amount (if available) - shown at top, marked as selected
    if (hasToken && token) {
      items.push({
        label: 'Pay Now',
        amount: `₹${(token.amount ?? 0).toFixed(2)}`,
        sublabel: token.validUntil ? `Valid until ${formatDate(token.validUntil)}` : undefined,
        isActive: true,
      });
    }

    // For ONE_TIME: show remaining amount (discountedMrp) with token's validUntil as due date
    if (isOneTime) {
      if (hasToken){
        const remainingDueDate = hasToken && token ? token.validUntil : null;
        items.push({
          label: 'Remaining Amount',
          amount: `₹${((totalAmount ?? 0) - (hasToken && token ? (token.amount ?? 0) : 0)).toFixed(2)}`,
          sublabel: remainingDueDate 
          ? `Due: ${formatDate(remainingDueDate)}` 
          : 'Full payment required',
          isActive: !hasToken,
        });
      } else {
        // For ONE_TIME without token: use first installment's due date if available
        const firstInstallment = (paymentCalendar?.installments?.length ?? 0) > 0 ? paymentCalendar!.installments![0] : null;
        items.push({
          label: 'Pay Now',
          amount: `₹${(totalAmount ?? 0).toFixed(2)}`,
          sublabel: firstInstallment?.dueDate ? `Due: ${formatDate(firstInstallment.dueDate)}` : undefined,
          isActive: true,
        });
      }
    }

    // For PART_PAYMENT: show installments
    if (isPartPayment) {
      (paymentCalendar?.installments ?? []).forEach((installment, idx) => {
        const isFirstInstallment = idx === 0;
        const label = hasToken 
          ? `Installment ${installment.installmentNumber}`
          : (isFirstInstallment ? 'Pay Now' : `Installment ${installment.installmentNumber}`);

        items.push({
          label,
          amount: `₹${(installment.amount ?? 0).toFixed(2)}`,
          sublabel: installment.dueDate ? `Due: ${formatDate(installment.dueDate)}` : undefined,
          isActive: isFirstInstallment && !hasToken,
        });
      });
    }

    return items;
  }, [token, paymentCalendar, isOneTime, isPartPayment, hasToken, totalAmount]);

  const computedTagLabel = isOneTime ? 'One-time Payment' : `${paymentPlan?.partPaymentCount || 1} Installments`;
  const computedDescription = isOneTime ? 'Single full payment' : 'Split into flexible installments';
  const computedFeatures = ['Fastest daily routes', 'Premium safety standards', 'Priority support'];

  // Extract policies from paymentPlan
  const policies = paymentPlanFromProp?.policies;
  
  // Define policy display labels
  const policyLabels: Record<string, string> = {
    cancellationPolicy: 'Cancellation Policy',
    discountPolicy: 'Discount Policy',
    planChangePolicy: 'Plan Change Policy',
    refundPolicy: 'Refund Policy',
    tokenPolicy: 'Token Policy',
  };

  // Build policy items array - only include policies that have values
  const policyItems = useMemo(() => {
    if (!policies) return [];
    
    const items: { key: string; label: string; text: string }[] = [];
    
    Object.entries(policies).forEach(([key, value]) => {
      if (value && typeof value === 'string' && policyLabels[key]) {
        items.push({
          key,
          label: policyLabels[key],
          text: value,
        });
      }
    });
    
    return items;
  }, [policies]);

  // State for managing open tooltip
  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    getCalculatedValues: () => ({
      tokenAmount: hasToken && token ? (token.amount ?? 0).toFixed(2) : '0.00',
      remainingAmount: ((totalAmount ?? 0) - (hasToken && token ? (token.amount ?? 0) : 0)).toFixed(2),
      discountAmount: (mrp ?? 0) - (totalAmount ?? 0),
      discountPercent,
      totalAmount: totalAmount ?? 0,
      payNowAmount,
    }),
  }));

  return (
    <div
      className={`plan-card ${colorClass} ${isSelected ? 'active' : ''}`}
      onClick={onSelect}
    >
      {/* Best Value Badge */}
      {isBestValue && (
        <div className="plan-card__best-value-badge">
          <StarFilled style={{ fontSize: 11 }} />
          Best Value
        </div>
      )}

      <div className="plan-card__body">
        <div className="plan-card__header-row">
          <div className="plan-card__header-left">
            <h3 className="plan-card__title">{paymentPlan?.name}</h3>
            <div className="plan-card__badges">
              {discountPercent > 0 && (
                <span className="plan-card__savings-inline">Save {discountPercent.toFixed(0)}%</span>
              )}
              <span className="plan-card__tag">{computedTagLabel}</span>
            </div>
          </div>
          <div className="plan-card__total-amount">
            <span className="plan-card__total-value">₹{(totalAmount ?? 0).toLocaleString('en-IN')}</span>
            {(mrp ?? 0) > (totalAmount ?? 0) && (
              <span className="plan-card__original-mrp">₹{(mrp ?? 0).toLocaleString('en-IN')}</span>
            )}
          </div>
        </div>

        {/* Description */}
        <p className="plan-card__description">{computedDescription}</p>

        {/* Payment Schedule */}
        {scheduleItems.length > 0 && (
          <div className="plan-card__payment-schedule">
            <h4 className="plan-card__schedule-title">Payment Breakdown</h4>
            {scheduleItems.map((item, idx) => (
              <div key={idx} className="plan-card__schedule-item">
                <div className="plan-card__schedule-info">
                  <p className="plan-card__schedule-label">
                    {item.label}: <strong>{item.amount}</strong>
                  </p>
                  {item.sublabel && (
                    <p className="plan-card__schedule-sublabel">{item.sublabel}</p>
                  )}
                </div>
                <span className={`plan-card__schedule-dot ${item.isActive ? 'active' : ''}`}></span>
              </div>
            ))}
          </div>
        )}

        {/* Features */}
        <div className="plan-card__features">
          {computedFeatures.map((feat, idx) => (
            <div key={idx} className="plan-card__feature-item">
              <CheckOutlined className="plan-card__feature-icon" />
              <span>{feat}</span>
            </div>
          ))}
        </div>

        {/* Policies Section */}
        {policyItems.length > 0 && (
          <div className="plan-card__policies">
            <h4 className="plan-card__policies-title">Policies</h4>
            <div className="plan-card__policies-list">
              {policyItems.map((policy) => (
                <div key={policy.key} className="plan-card__policy-item">
                  <span className="plan-card__policy-label">{policy.label}</span>
                  <Tooltip 
                    title={policy.text}
                    open={openTooltipKey === policy.key}
                    onOpenChange={(open) => setOpenTooltipKey(open ? policy.key : null)}
                    trigger="click"
                    overlayClassName="plan-card__policy-tooltip"
                  >
                    <InfoCircleOutlined 
                      className="plan-card__policy-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTooltipKey(openTooltipKey === policy.key ? null : policy.key);
                      }}
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="plan-card__selected-indicator">
          <CheckOutlined style={{ fontSize: 12 }} />
        </div>
      )}
    </div>
  );
});

PlanCard.displayName = 'PlanCard';
